import { Autocomplete } from '@react-google-maps/api';
import React, { useRef, useEffect } from 'react';

interface GeriatricsFormProps {
    form: {
        nombre: string;
        direccion: string;
        diasHorarios: string;
        activo: boolean;
        hermanosAsignados: string[];
        estado: string;
        telefono: string;
        territorio?: string;
    };
    onFormChange: (updater: (prev: any) => any) => void;
    onSubmit: (e: React.FormEvent) => void;
    onCancel: () => void;
    editId: number | null;
    loading: boolean;
    geocodingLoading: boolean;
    isDarkMode: boolean;
    setAutocomplete: (ac: google.maps.places.Autocomplete) => void;
    autocomplete: google.maps.places.Autocomplete | null;
}

export const GeriatricsForm: React.FC<GeriatricsFormProps> = ({
    form,
    onFormChange,
    onSubmit,
    onCancel,
    editId,
    loading,
    geocodingLoading,
    isDarkMode,
    setAutocomplete,
    autocomplete
}) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    return (
        <form className={`mb-3 card ${isDarkMode ? 'bg-lightdark text-white border-light' : 'bg-light border'} p-3 mt-5 border-1 rounded-6 shadow-sm`} onSubmit={onSubmit}>
            <div className="row g-2">
                <div className="col-md-6">
                    <label className="form-label">Nombre del Geriátrico</label>
                    <input 
                        ref={inputRef} 
                        required 
                        className="form-control" 
                        placeholder="Nombre" 
                        value={form.nombre} 
                        onChange={e => onFormChange(f => ({ ...f, nombre: e.target.value }))} 
                    />
                </div>
                <div className="col-md-6">
                    <label className="form-label">Territorio</label>
                    <input 
                        className="form-control" 
                        placeholder="Nro Territorio" 
                        value={form.territorio || ''} 
                        onChange={e => onFormChange(f => ({ ...f, territorio: e.target.value }))} 
                    />
                </div>
                <div className="col-md-6">
                    <label className="form-label">Dirección</label>
                    <Autocomplete
                        onLoad={ac => setAutocomplete(ac)}
                        onPlaceChanged={() => {
                            if (autocomplete) {
                                const place = autocomplete.getPlace();
                                const direccion = place.formatted_address || place.name || '';
                                onFormChange(f => ({ ...f, direccion }));
                            }
                        }}
                    >
                        <input
                            required
                            className="form-control"
                            placeholder="Dirección"
                            value={form.direccion}
                            onChange={e => onFormChange(f => ({ ...f, direccion: e.target.value }))}
                            type="text"
                        />
                    </Autocomplete>
                </div>
                
                <div className="col-12 mt-3">
                    <label className="form-label">Hermanos Asignados (1 a 4)</label>
                    <div className="row g-2">
                        {[0, 1, 2, 3].map((index) => (
                            <div className="col-md-3" key={index}>
                                <input
                                    className="form-control form-control-sm"
                                    placeholder={`Hermano ${index + 1}`}
                                    value={form.hermanosAsignados[index]}
                                    onChange={e => {
                                        const newHermanos = [...form.hermanosAsignados];
                                        newHermanos[index] = e.target.value;
                                        onFormChange(f => ({ ...f, hermanosAsignados: newHermanos }));
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="col-md-6 mt-3">
                    <label className="form-label">Días y horarios de visita</label>
                    <input 
                        className="form-control" 
                        placeholder="Ej: Lunes 10hs" 
                        value={form.diasHorarios} 
                        onChange={e => onFormChange(f => ({ ...f, diasHorarios: e.target.value }))} 
                    />
                </div>

                <div className="col-md-6 mt-3">
                    <label className="form-label">Teléfono</label>
                    <input 
                        className="form-control" 
                        placeholder="Teléfono" 
                        value={form.telefono} 
                        onChange={e => onFormChange(f => ({ ...f, telefono: e.target.value }))} 
                    />
                </div>

                <div className="col-12 mt-3">
                    <label className="form-label">Estado / Progreso</label>
                    <textarea
                        className="form-control"
                        rows={3}
                        placeholder="Información sobre el estado o progreso..."
                        value={form.estado || ''}
                        onChange={e => onFormChange(f => ({ ...f, estado: e.target.value }))}
                    ></textarea>
                </div>

                <div className="col-12 d-flex justify-content-end gap-2 mt-3">
                    <button className="btn btn-outline-secondary" type="button" onClick={onCancel} disabled={loading}>
                        Cancelar
                    </button>
                    <button className="btn btn-general-blue" type="submit" disabled={geocodingLoading || loading}>
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {editId ? 'Modificando...' : 'Agregando...'}
                            </>
                        ) : (
                            editId ? 'Modificar' : 'Agregar'
                        )}
                    </button>
                </div>
            </div>
        </form>
    );
};
